import type React from "react"
import { useEffect, useState } from "react"
import {
  Paper,
  Typography,
  Box,
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tooltip,
  IconButton,
  Tabs,
  Tab,
  Select,
  FormControl,
  MenuItem,
} from "@mui/material"
import { FileDownload, PictureAsPdf, Delete } from "@mui/icons-material"
import * as XLSX from "xlsx"
import jsPDF from "jspdf"
import autoTable from "jspdf-autotable"
import type { PollResponse } from "../../types"
import { DeletePoll, GetPollAdmin, type GetPollPayload } from "../../utils/APIRequest"
import { formatDate } from "../../utils/CommonMethods"
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import {
  ArrowBack as ArrowBackIcon,
  Restaurant as RestaurantIcon,
} from "@mui/icons-material"
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import LunchOptedUser from "./LunchOptedUser"
import BulletinBoardAdmin from "../../screens/BulletinBoardAdmin"
import AdminJournals from "./AdminJournals";

const AdminDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<"polls" | "users" | "bulletin" | "journals">("polls")
  const [pollResponses, setPollResponses] = useState<PollResponse[]>([])
  const [selectedDay, setSelectedDay] = useState<"today" | "tomorrow">("today")
  const navigate = useNavigate();
  const theme = useTheme();

  // This will determine the actual date used in fetch
  const getSelectedDate = (): string => {
    const date = new Date();
    if (selectedDay === "tomorrow") {
      date.setDate(date.getDate() + 1);
    }
    return formatDate(date);
  };

  // Function to fetch poll responses from the API
  const fetchPollResponses = async () => {
    const dateToFetch = getSelectedDate();
    const pollPayload: GetPollPayload = {
      date: dateToFetch,
    };
    const response = await GetPollAdmin(pollPayload);

    if (response && response.statusCode === 200) {
      if (Array.isArray(response.data)) {
        setPollResponses(response.data);
      } else {
        setPollResponses([]);
      }
    } else {
      console.error("Error fetching poll responses:", response);
    }
  }

  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(
      pollResponses.map((response) => ({
        Name: response.emp_name,
        Attending: response.foodChoice !== "No" ? "Yes" : "No",
        Preference: response.foodChoice !== "No" ? response.foodChoice : "N/A",
      })),
    )

    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, "Lunch Poll")
    XLSX.writeFile(workbook, "lunch_poll_results.xlsx")
  }

  const exportToPDF = () => {
    const doc = new jsPDF()

    doc.setFontSize(18)
    doc.text("Lunch Poll Results", 14, 22)
    doc.setFontSize(11)
    doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 30)

    const attending = pollResponses.filter((r) => r.foodChoice !== "No").length
    const vegCount = pollResponses.filter((r) => r.foodChoice === "Veg").length
    const nonVegCount = pollResponses.filter((r) => r.foodChoice === "Non-Veg").length

    doc.setFontSize(14)
    doc.text("Summary", 14, 45)
    doc.setFontSize(11)
    doc.text(`Total Responses: ${pollResponses.length}`, 14, 55)
    doc.text(`Attending: ${attending}`, 14, 62)
    doc.text(`Not Attending: ${pollResponses.length - attending}`, 14, 69)
    doc.text(`Vegetarian: ${vegCount}`, 14, 76)
    doc.text(`Non-Vegetarian: ${nonVegCount}`, 14, 83)

    const tableColumn = ["Name", "Attending", "Preference"]
    const tableRows = pollResponses.map((response) => [
      response.emp_name,
      response.foodChoice !== "No" ? "Yes" : "No",
      response.foodChoice !== "No" ? response.foodChoice : "N/A",
    ])

    autoTable(doc, {
      startY: 95,
      head: [tableColumn],
      body: tableRows,
      theme: "striped",
      headStyles: { fillColor: [66, 139, 202] },
      styles: {
        fontSize: 10,
        cellPadding: 3,
      },
      columnStyles: {
        0: { cellWidth: 60 },
        1: { cellWidth: 30 },
        2: { cellWidth: 40 },
      },
    })

    doc.save("lunch_poll_results.pdf")
  }

  const handleDeleteResponse = async (username: string) => {
    try {
      const response = await DeletePoll({ username: username })
      if (response && response.statusCode === 200) {
        fetchPollResponses()
      }
    } catch (error) {
      console.error("Error deleting response:", error)
    }
  }

  useEffect(() => {
    if (activeTab === "polls") {
      fetchPollResponses()
    }
  }, [activeTab, selectedDay])

  const handleTabChange = (event: React.SyntheticEvent, newValue: "polls" | "users" | "bulletin") => {
    setActiveTab(newValue)
  }

  return (
    <Paper elevation={3} sx={{ p: 3, mt: 4 }}>
      {/* Header */}
      <AppBar position="fixed" elevation={0} sx={{ background: `linear-gradient(135deg,${theme.palette.primary.main},${theme.palette.primary.dark})` }}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => navigate("/")}>
            <ArrowBackIcon />
          </IconButton>
          <RestaurantIcon sx={{ mr: 2, color: theme.palette.secondary.contrastText }} />
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 600, color: theme.palette.secondary.contrastText }}>
            Lunch Poll
          </Typography>
        </Toolbar>
      </AppBar>
      <Box >
        {/* Navigation Tabs */}
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{ mb: 3, borderBottom: 1, borderColor: "divider" }}
        >
          <Tab
            value="polls"
            label="Poll Management"
            sx={{ textTransform: "none", fontSize: "1rem" }}
          />
          <Tab
            value="users"
            label="User Management"
            sx={{ textTransform: "none", fontSize: "1rem" }}
          />
        </Tabs>

        {/* Tab Content */}
        {activeTab === "polls" ? (
          <>
            <Typography variant="h6" gutterBottom>
              Lunch Poll Results
            </Typography>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 3,
              }}
            >
              <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<FileDownload />}
                  onClick={exportToExcel}
                  disabled={pollResponses.length === 0}
                >
                  Export to Excel
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<PictureAsPdf />}
                  onClick={exportToPDF}
                  disabled={pollResponses.length === 0}
                >
                  Export to PDF
                </Button>
              </Stack>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <Select
                  labelId="poll-date-select-label"
                  value={selectedDay}
                  label="Select Date"
                  onChange={(e) => {
                    setSelectedDay(e.target.value as "today" | "tomorrow");
                  }}
                >
                  <MenuItem value="today">Today</MenuItem>
                  <MenuItem value="tomorrow">Tomorrow</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Typography variant="subtitle1" gutterBottom>
              Total Responses: {pollResponses.length}
            </Typography>
            <TableContainer component={Paper} elevation={2}>
              <Table sx={{ minWidth: 650 }} aria-label="poll responses table">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "primary.light" }}>
                    <TableCell sx={{ color: "white" }}>Employee Name</TableCell>
                    <TableCell sx={{ color: "white" }}>Attending</TableCell>
                    <TableCell sx={{ color: "white" }}>Preference</TableCell>
                    <TableCell sx={{ color: "white" }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pollResponses.length > 0 ? (
                    pollResponses.map((response) => (
                      <TableRow key={response.name}>
                        <TableCell>{response.emp_name}</TableCell>
                        <TableCell>
                          <Chip
                            label={response.foodChoice !== "No" ? "Yes" : "No"}
                            color={
                              response.foodChoice !== "No" ? "success" : "error"
                            }
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {response.foodChoice !== "No" ? (
                            <Chip
                              label={
                                response.foodChoice === "Veg"
                                  ? "Vegetarian"
                                  : "Non-Vegetarian"
                              }
                              color={
                                response.foodChoice === "Veg"
                                  ? "secondary"
                                  : "primary"
                              }
                              size="small"
                            />
                          ) : (
                            "N/A"
                          )}
                        </TableCell>
                        <TableCell>
                          <Tooltip title="Delete Response">
                            <IconButton
                              color="error"
                              onClick={() =>
                                handleDeleteResponse(response.name)
                              }
                              size="small"
                            >
                              <Delete />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        No polls available!
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        ) : activeTab === "users" ? (
          <LunchOptedUser />
        ) : activeTab === "journals" ? (
          <AdminJournals />
        ) : (
          <BulletinBoardAdmin />
        )}
      </Box>
    </Paper>
  );
}

export default AdminDashboard
